/**
 * Agent Fiona's card actions (store_management, AGS-896).
 *
 * The cards themselves come in GET /rule-engine-decisions/{store}, with the
 * other agents'. These routes answer and follow them up; every one ends in
 * {store} (the authorizer scopes on it) and takes ?marketplace=. A request
 * the card can't take answers 400, 404 or 409 with {detail}, which is thrown
 * as the error's message.
 *
 * POST /fiona-cards/decision/{store}
 *   {asin, card_group, rule_engine_decision_id, response: accept | reject,
 *    status, show_again_at}   (accept a packaging card)
 *   {..., response: accept}   (a price card: closes straight away)
 *   {..., response: reject, reject_reason, reject_note}
 *   → {cards: [card]}: every card changed (Discontinued: one per marketplace)
 * POST /fiona-cards/status/{store} {asin, card_group, status?, show_again_at?}
 *   → {card}
 * POST /fiona-cards/reviewed/{store} {asin, card_group} → {card} (closed)
 * GET /fiona-cards/options/{store} → {accepted_statuses, closed_on_accept,
 *   reject_reasons: [{code, label, note_required, all_marketplaces, excludes}]}
 *
 * A returned card: {status, card_type, show_again_at, status_changed_at,
 * decided_by, rule_engine_decision_id, reject_reason, reject_note, asin,
 * card_group, store, marketplace_id}.
 */

import { atomicConfig } from "../../../../../../config";
import { call_api_auth } from "../../../../../../utils/services";

const url = (route, store, marketplace, query = {}) => {
  const params = new URLSearchParams({
    marketplace: marketplace || "us",
    ...query,
  });
  return `${atomicConfig.storeManagementServiceUrl}/fiona-cards/${route}/${store}?${params}`;
};

// The backend's {detail} when there is one
const errorMessage = (error) =>
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong";

const request = async (method, address, body) => {
  try {
    const result = await call_api_auth(
      address,
      method,
      body ? JSON.stringify(body) : undefined
    );
    return result?.data;
  } catch (error) {
    throw new Error(errorMessage(error));
  }
};

const cardKey = (decision) => ({
  asin: decision.asin,
  card_group: decision.decision?.card_group || "fiona",
});

export const acceptFionaCard = (
  decision,
  { store, marketplace, status, showAgainAt }
) =>
  request("POST", url("decision", store, marketplace), {
    ...cardKey(decision),
    rule_engine_decision_id: decision.fiona_card?.decision_id,
    response: "accept",
    ...(status ? { status, show_again_at: showAgainAt } : {}),
  });

export const rejectFionaCard = (
  decision,
  { store, marketplace, rejectReason, rejectNote }
) =>
  request("POST", url("decision", store, marketplace), {
    ...cardKey(decision),
    rule_engine_decision_id: decision.fiona_card?.decision_id,
    response: "reject",
    reject_reason: rejectReason,
    ...(rejectNote ? { reject_note: rejectNote } : {}),
  });

export const updateFionaCardStatus = (
  decision,
  { store, marketplace, status, showAgainAt }
) =>
  request("POST", url("status", store, marketplace), {
    ...cardKey(decision),
    ...(status ? { status } : {}),
    ...(showAgainAt ? { show_again_at: showAgainAt } : {}),
  });

export const markFionaCardReviewed = (decision, { store, marketplace }) =>
  request("POST", url("reviewed", store, marketplace), cardKey(decision));

// The options are the same for every store and change only with a deploy,
// so one request serves the whole session
let optionsRequest = null;
export const fetchFionaOptions = (store, marketplace) => {
  if (!optionsRequest) {
    optionsRequest = request("GET", url("options", store, marketplace)).catch(
      (error) => {
        optionsRequest = null;
        throw error;
      }
    );
  }
  return optionsRequest;
};
